import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ThemeProvider } from "./contexts/ThemeContext";
import { isCustomer, isTenantAdmin, isEmployee } from "./utils/userHelper";
import { isDemoMode, getDemoRole } from "./utils/demoMode";
import { mockUser, mockTenantAdminUser } from "./services/mockData";
import { setDemoUser } from "./store/slices/authSlice";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Tenants from "./pages/Tenants";
import Users from "./pages/Users";
import PendingUsers from "./pages/PendingUsers";
import Appointments from "./pages/Appointments";
import Services from "./pages/Services";
import Categories from "./pages/Categories";
import Exceptions from "./pages/Exceptions";
import PrivacyPolise from "./pages/PrivacyPolise";
import Home from "./pages/Home";
import About from "./pages/About";
import LookupTables from "./pages/LookupTables";
import Calendar from "./pages/AppointmentCalendar";
import TestPage from "./pages/TestPage";
import TenantSettings from "./pages/TenantSettings";
import AppointmentBooking from "./pages/AppointmentBooking";
import LiveDashboard from "./pages/LiveDashboard";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import UserRegistration from "./pages/UserRegistration";
import Homepage from "./pages/homepage";
import LastAndNextAppointment from "./pages/LastAndNextAppointment";
import Statistics from "./pages/Statistics";
import DemoLanding from "./pages/DemoLanding";
import CoopRequests from "./pages/CoopRequests";
import Demo, { DemoThankYou } from "./pages/Demo";

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user, shouldRedirectToDashboard } = useSelector(
    (state) => state.auth,
  );

  // Uvek očisti demo podatke na početku aplikacije
  // Demo podaci će se ponovo postaviti samo ako korisnik ode na /demo rutu
  useEffect(() => {
    const user = localStorage.getItem('user');
    
    // Proveri da li je demo user
    let isDemoUser = false;
    if (user) {
      try {
        const userObj = JSON.parse(user);
        isDemoUser = userObj && (userObj._id === 'demo-user-1' || userObj._id === 'demo-admin-1');
      } catch (e) {
        // Ako ne može da parsira, ignoriši
      }
    }
    
    // Očisti demo podatke
    localStorage.removeItem('demoMode');
    localStorage.removeItem('demoRole');
    if (isDemoUser) {
      localStorage.removeItem('user');
    }
    console.log("remove all demo data");
    
    // Očisti demo storage podatke
    const demoStorageKeys = [
      'demo_services',
      'demo_categories',
      'demo_appointments',
      'demo_exceptions',
      'demo_employees',
      'demo_customers',
      'demo_tenant'
    ];
    demoStorageKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  }, []);

  // Postavi demo user-a u Redux store ako je demo mode
  useEffect(() => {
    if (isDemoMode() && !isAuthenticated) {
      // Resetuj demo storage na početne vrednosti svaki put kada se aplikacija učita
      const { initializeDemoStorage } = require("./services/demoStorage");
      const mockData = require("./services/mockData");
      initializeDemoStorage(mockData);

      // Postavi odgovarajućeg demo user-a na osnovu role-a
      const role = getDemoRole();
      if (role === "owner") {
        dispatch(setDemoUser(mockTenantAdminUser));
      } else {
        dispatch(setDemoUser(mockUser));
      }
    }
  }, [dispatch, isAuthenticated]);
  // Ukloni demo podatke iz localStorage kada korisnik napušta stranicu
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Proveri da li je demo mode aktivan
      const demoMode = localStorage.getItem('demoMode') === 'true';
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (demoMode) {
        // Ukloni demo flag-ove
        localStorage.removeItem('demoMode');
        localStorage.removeItem('demoRole');
        
        // Ukloni token ako postoji (demo mode ne koristi token)
        if (token) {
          localStorage.removeItem('token');
        }
        
        // Ukloni demo user-a ako postoji
        if (user) {
          try {
            const userObj = JSON.parse(user);
            // Proveri da li je demo user (mockUser ili mockTenantAdminUser)
            const isDemoUser = userObj._id === 'demo-user-1' || userObj._id === 'demo-admin-1';
            if (isDemoUser) {
              localStorage.removeItem('user');
            }
          } catch (e) {
            // Ako ne može da parsira user, ukloni ga
            localStorage.removeItem('user');
          }
        }
        
        // Ukloni sve demo storage podatke
        const demoStorageKeys = [
          'demo_services',
          'demo_categories',
          'demo_appointments',
          'demo_exceptions',
          'demo_employees',
          'demo_customers',
          'demo_tenant'
        ];
        demoStorageKeys.forEach(key => {
          localStorage.removeItem(key);
        });
      }
    };
    console.log("delete demo user");
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);



  // Helper funkcija za odredjivanje da li je korisnik customer ili ne
  // Koristi helper funkciju koja proverava tenantConnections
  const checkIsCustomer = () => {
    if (!user) return true; // Ako nema user-a, tretiraj kao customer
    // Koristi helper funkciju koja proverava tenantConnections
    return isCustomer(user);
  };

  // Helper funkcija za odredjivanje gde da se preusmeri korisnik
  const getRedirectPath = () => {
    // Za tenant_admin i employee, idi na live-dashboard
    if (user && (isTenantAdmin(user) || isEmployee(user))) {
      return "/live-dashboard";
    }
    if (!checkIsCustomer()) {
      return "/dashboard";
    }
    if (shouldRedirectToDashboard) {
      return "/dashboard";
    }
    return "/homepage";
  };

  return (
    <ThemeProvider>
      <Routes>
        <Route
          path="/login"
          element={
            !isAuthenticated ? <Login /> : <Navigate to={getRedirectPath()} />
          }
        />
        <Route
          path="/"
          element={
            <Navigate to={!isAuthenticated ? "/homepage" : getRedirectPath()} />
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/statistics"
          element={
            <ProtectedRoute>
              <Layout>
                <Statistics />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/last-and-next-appointment"
          element={
            <ProtectedRoute customerOnly>
              <Layout>
                <LastAndNextAppointment />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/homepage"
          element={
            <ProtectedRoute customerOnly>
              <Layout>
                <Homepage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/invite-user/:tenantId" element={<UserRegistration />} />
        <Route
          path="/tenants"
          element={
            <ProtectedRoute>
              <Layout>
                <Tenants />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <Layout>
                <Users />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute>
              <Layout>
                <Users />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pending-users"
          element={
            <ProtectedRoute>
              <Layout>
                <PendingUsers />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/appointments"
          element={
            <ProtectedRoute>
              <Layout>
                <Appointments />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <Layout>
                <Calendar />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/services"
          element={
            <ProtectedRoute>
              <Layout>
                <Services />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <ProtectedRoute>
              <Layout>
                <Categories />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/exceptions"
          element={
            <ProtectedRoute>
              <Layout>
                <Exceptions />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy"
          element={
            <ProtectedRoute>
              <Layout>
                <PrivacyPolise />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lookup-tables"
          element={
            <ProtectedRoute>
              <Layout>
                <LookupTables />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant-settings"
          element={
            <ProtectedRoute>
              <Layout>
                <TenantSettings />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant-edit/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <TenantSettings isEdit />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/appointment-booking/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <AppointmentBooking />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/test"
          element={
            <ProtectedRoute>
              <TestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/live-dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <LiveDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/userregistration" element={<UserRegistration />} />
        <Route path="/home" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/cooperation" element={<Demo />} />
        <Route path="/privacy-policy" element={<PrivacyPolise />} />
        {/* Demo rute */}
        <Route path="/demo" element={<DemoLanding />} />
        <Route path="/demo-request" element={<Demo />} />
        <Route path="/demo-request/thanks" element={<DemoThankYou />} />
        <Route
          path="/demo/last-and-next-appointment"
          element={
            <Layout>
              <LastAndNextAppointment />
            </Layout>
          }
        />
        <Route
          path="/demo/appointment-booking/:id"
          element={
            <Layout>
              <AppointmentBooking />
            </Layout>
          }
        />
        <Route
          path="/demo/appointments"
          element={
            <Layout>
              <Appointments />
            </Layout>
          }
        />
        <Route
          path="/demo/live-dashboard"
          element={
            <Layout>
              <LiveDashboard />
            </Layout>
          }
        />
        <Route
          path="/demo/services"
          element={
            <Layout>
              <Services />
            </Layout>
          }
        />
        <Route
          path="/demo/categories"
          element={
            <Layout>
              <Categories />
            </Layout>
          }
        />
        <Route
          path="/demo/exceptions"
          element={
            <Layout>
              <Exceptions />
            </Layout>
          }
        />
        <Route
          path="/demo/tenant-settings"
          element={
            <Layout>
              <TenantSettings />
            </Layout>
          }
        />
        <Route
          path="/demo/employees"
          element={
            <Layout>
              <Users />
            </Layout>
          }
        />
        <Route
          path="/demo/calendar"
          element={
            <Layout>
              <Calendar />
            </Layout>
          }
        />
        <Route
          path="/coop-requests"
          element={
            <ProtectedRoute>
              <Layout>
                <CoopRequests />
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
